import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Divider,
} from '@mui/material';
import { AccountCircle, ExitToApp, Dashboard, Person, Rule, AutoGraph, Flag } from '@mui/icons-material';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { useUpdateOnboardingStateMutation } from '../../store/api/githubApi';
import { GettingStartedChecklist } from '../onboarding/GettingStartedChecklist';

interface NavLinkItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
}

export const DashboardLayout: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [updateOnboarding] = useUpdateOnboardingStateMutation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const safeName = (user?.github_login ?? '').trim();
  const initial = safeName ? safeName.charAt(0).toUpperCase() : null;

  const navLinks: NavLinkItem[] = [
    { label: 'Dashboard', path: '/dashboard', icon: <Dashboard fontSize="small" /> },
    { label: 'Remediation Planner', path: '/remediation-planner', icon: <AutoGraph fontSize="small" /> },
    { label: 'Policies', path: '/policies', icon: <Rule fontSize="small" /> },
  ];

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    dispatch(logout());
    navigate('/auth/login');
    handleClose();
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        /* Layered radial-gradient wash + white base */
        background: `
          radial-gradient(circle at 20% 8%, rgba(220, 236, 255, 0.40), transparent 36%),
          radial-gradient(circle at 82% 38%, rgba(234, 242, 255, 0.35), transparent 34%),
          radial-gradient(circle at 50% 85%, rgba(238, 244, 250, 0.25), transparent 32%),
          #f8fafc
        `,
      }}
    >
      {/* ── Animated floating background orbs ───────────────────────────── */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
          /* Respect prefers-reduced-motion */
          '@media (prefers-reduced-motion: reduce)': {
            '& > *': { animation: 'none !important' },
          },
          /* Keyframes — GPU-friendly transform + scale only */
          '@keyframes floatOrb1': {
            '0%': { transform: 'translate3d(0, 0, 0) scale(1)' },
            '33%': { transform: 'translate3d(30px, -20px, 0) scale(1.04)' },
            '66%': { transform: 'translate3d(-15px, 25px, 0) scale(0.97)' },
            '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
          },
          '@keyframes floatOrb2': {
            '0%': { transform: 'translate3d(0, 0, 0) scale(1)' },
            '33%': { transform: 'translate3d(-25px, 30px, 0) scale(1.06)' },
            '66%': { transform: 'translate3d(20px, -15px, 0) scale(0.96)' },
            '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
          },
          '@keyframes floatOrb3': {
            '0%': { transform: 'translate3d(0, 0, 0) scale(1)' },
            '40%': { transform: 'translate3d(18px, 28px, 0) scale(1.03)' },
            '70%': { transform: 'translate3d(-22px, -12px, 0) scale(0.98)' },
            '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
          },
        }}
      >
        {/* Orb 1 — top-left, large blue */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            left: '8%',
            width: 440,
            height: 440,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(220, 236, 255, 0.50) 0%, transparent 70%)',
            filter: 'blur(80px)',
            opacity: 0.18,
            animation: 'floatOrb1 24s ease-in-out infinite',
            willChange: 'transform',
          }}
        />
        {/* Orb 2 — top-right, soft blue */}
        <Box
          sx={{
            position: 'absolute',
            top: '-4%',
            right: '4%',
            width: 380,
            height: 380,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(234, 242, 255, 0.55) 0%, transparent 70%)',
            filter: 'blur(75px)',
            opacity: 0.16,
            animation: 'floatOrb2 28s ease-in-out infinite',
            animationDelay: '-8s',
            willChange: 'transform',
          }}
        />
        {/* Orb 3 — center-right, subtle gray-blue */}
        <Box
          sx={{
            position: 'absolute',
            top: '32%',
            right: '-3%',
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(191, 219, 254, 0.40) 0%, transparent 70%)',
            filter: 'blur(70px)',
            opacity: 0.14,
            animation: 'floatOrb3 22s ease-in-out infinite',
            animationDelay: '-4s',
            willChange: 'transform',
          }}
        />
        {/* Orb 4 — bottom-left, light blue */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '8%',
            left: '5%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(220, 236, 255, 0.45) 0%, transparent 70%)',
            filter: 'blur(65px)',
            opacity: 0.15,
            animation: 'floatOrb2 26s ease-in-out infinite',
            animationDelay: '-14s',
            willChange: 'transform',
          }}
        />
        {/* Orb 5 — bottom-center, very subtle neutral */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-5%',
            left: '40%',
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(237, 243, 250, 0.40) 0%, transparent 70%)',
            filter: 'blur(60px)',
            opacity: 0.12,
            animation: 'floatOrb1 20s ease-in-out infinite',
            animationDelay: '-10s',
            willChange: 'transform',
          }}
        />
      </Box>

      {/* ── Floating Glass Header ───────────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          display: 'flex',
          justifyContent: 'center',
          pt: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1400,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: 1.25,
            borderRadius: '18px',
            /* Translucent glass effect — background visible through */
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.48), rgba(255, 255, 255, 0.22))',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(255, 255, 255, 0.55)',
            boxShadow: '0 8px 30px rgba(30, 64, 175, 0.06)',
          }}
        >
          {/* Logo — clickable, always goes to dashboard */}
          <Box
            component="img"
            src="/static/logo.svg"
            alt="KodeGauge"
            sx={{ height: 32, cursor: 'pointer', mr: 1.5, flexShrink: 0 }}
            onClick={() => navigate('/dashboard')}
          />

          {/* Primary nav links */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {navLinks.map(({ label, path, icon, badge }) => {
              const active = location.pathname === path || location.pathname.startsWith(path + '/');
              return (
                <Button
                  key={path}
                  startIcon={icon}
                  onClick={() => navigate(path)}
                  sx={{
                    color: active ? '#2563eb' : '#475569',
                    fontWeight: active ? 700 : 500,
                    borderRadius: '10px',
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease',
                    backgroundColor: active ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                    '&:hover': {
                      color: '#2563eb',
                      backgroundColor: 'rgba(37, 99, 235, 0.06)',
                    },
                    '& .MuiButton-startIcon': {
                      color: active ? '#2563eb' : '#64748b',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{label}</span>
                    {badge !== undefined && (
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          borderRadius: '10px',
                          px: 0.75,
                          py: 0.15,
                          lineHeight: 1.3,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 18,
                        }}
                      >
                        {badge}
                      </Box>
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* User menu */}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleMenu} size="small" sx={{ p: 0.5 }}>
                <Avatar
                  src={user.avatar_url ?? undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: '#f1f5f9',
                    color: '#475569',
                    fontSize: '0.875rem',
                    border: '1px solid #e2e8f0',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
                    },
                  }}
                >
                  {initial ?? <Person sx={{ fontSize: 20, color: '#475569' }} />}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      mt: 1.5,
                      minWidth: 200,
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.10), 0 2px 6px rgba(15, 23, 42, 0.04)',
                      borderRadius: '14px',
                    },
                  },
                }}
              >
                {/* Identity header */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>
                    {safeName || 'Account'}
                  </Box>
                  {user.email && (
                    <Box sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>{user.email}</Box>
                  )}
                </Box>
                <Divider sx={{ borderColor: '#e2e8f0' }} />
                <MenuItem
                  onClick={() => {
                    navigate('/profile');
                    handleClose();
                  }}
                  sx={{
                    gap: 1.5,
                    fontSize: '0.875rem',
                    color: '#334155',
                    py: 1,
                    '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.06)', color: '#2563eb' },
                  }}
                >
                  <AccountCircle fontSize="small" sx={{ color: '#64748b' }} />
                  Profile
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    updateOnboarding({ status: 'active' });
                    handleClose();
                  }}
                  sx={{
                    gap: 1.5,
                    fontSize: '0.875rem',
                    color: '#334155',
                    py: 1,
                    '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.06)', color: '#2563eb' },
                  }}
                >
                  <Flag fontSize="small" sx={{ color: '#64748b' }} />
                  Getting started
                </MenuItem>
                <Divider sx={{ borderColor: '#e2e8f0' }} />
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    gap: 1.5,
                    fontSize: '0.875rem',
                    color: '#ef4444',
                    py: 1,
                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' },
                  }}
                >
                  <ExitToApp fontSize="small" />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Page Content ────────────────────────────────────────────────── */}
      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          mb: 6,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Outlet />
      </Container>
      <GettingStartedChecklist />
    </Box>
  );
};
